import React, { useEffect } from 'react';
import './StandStateDiagramView.css';
import ScatterChart from 'components/charts/MultipleScatterChart/ScatterChart';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import CanopyCoverChart from 'components/charts/CanopyCoverChart/CanopyCoverChart';
import { Point, ILineData } from 'components/charts/MultipleScatterChart/components/Area';
import { CalculateCanopyCoverDBHRRBySpecies, GetCanopyCoverBySpecies, CalculatePurpleWye } from 'services/RunApplication';
import { calculate_reliability_index } from 'utils/reliability_index';
import Card from "react-bootstrap/Card";
import DropdownButton from "react-bootstrap/DropdownButton";
import Dropdown from "react-bootstrap/Dropdown";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';


const labels = { x: "DBH (cm)", y: "Stand Density (Tress/ha)" };

interface Props {
  currentSpecies: { name: string, index: number }
}

function StandStateDiagramViewRow({ currentSpecies }: Props) {

  const [data, setData] = React.useState<ILineData[]>([]);
  const [avgPoint, setAvgPoint] = React.useState<Point>([-1, -1]);
  const WASMApplicationState = useSelector((state: RootState) => state.WASMApplication);
  const results = useSelector((state: RootState) => state.WASMApplicationResult);
  const [rootReinforcementParams, setRRParams] = React.useState({ start: 0, end: 20000, step: 1000 });
  const [canopyCoverChartBeta, setCanopyCoverChartBeta] = React.useState<number[]>([]);
  const [canopyCoverChartDBH, setCanopyCoverChartDBH] = React.useState<Map<number, { lrr: number, canopy_cover: number }[]>>(new Map());
  const [markData, setMarkData] = React.useState<{ lrr: number, canopy_cover: number }>({ lrr: -1, canopy_cover: -1 });
  const [species, setSpecies] = React.useState<{ name: string, index: number }[]>([]);

  useEffect(() => {

    if (WASMApplicationState.species.length === 0 || currentSpecies.index === -1) { return; }


    function calculate_avg_point() {
      // Calculate the average point
      // - the y value (tree density) is the sum of the trees/ha of all species
      //- the x value (dbh) is a weighted sum: dbh = (DBH1 * SD1 + DBH2 * SD2 ... + DBH4*SD4)/ (SD1 + SD2 + SD3 + SD4) where SD are tree density values. Let me know if this is not clear 

      const avg_y = WASMApplicationState.speciesDensity[currentSpecies.index];

      if (avg_y === 0) {
        setAvgPoint([-1, -1]);
        return;
      }
      const keys = Object.keys(WASMApplicationState.speciesDensity);

      let avg_x = WASMApplicationState.treeDBH[currentSpecies.index];

      setAvgPoint([avg_x, avg_y]);
    }

    GetCanopyCoverBySpecies(WASMApplicationState, currentSpecies.index).then((returnMsg) => {

      calculate_avg_point();

      if (returnMsg.data.data.length === 0) {
        setData([]);
        return;
      }


      if (returnMsg.data.data.length === 0) {
        return;
      }
      console.log(returnMsg);

      let processed_data: { [key: string]: Point[] } = {};
      for (let d of returnMsg.data.data) {

        if (processed_data[d.canopy_cover] === undefined) {

          processed_data[d.canopy_cover] = [];
        }
        if (d.dbh < 0.05) {
          continue;
        }
        processed_data[d.canopy_cover].push([d.dbh * 100, d.mean_stand_density]);
      }

      const organized_data = []
      for (let k in processed_data) {
        const d: ILineData = {
          name: k + "%",
          points: processed_data[k],
          color: 'gray'
        }
        organized_data.push(d);
      }
      console.log(`Canopy Cover Processed By Bin:`)
      console.log(organized_data);
      setData(organized_data);
    })


  }, [WASMApplicationState.treeDBH, WASMApplicationState.speciesDensity, currentSpecies.index]);


  useEffect(() => {
    //Let's update the names we have available
    let species_used = []
    for (let k of Object.keys(WASMApplicationState.speciesDensity).map(Number)) {
      if (WASMApplicationState.speciesDensity[k] === 0) continue;

      species_used.push({ name: WASMApplicationState.species[k], index: k })
    }
    setSpecies(species_used);
  }, [WASMApplicationState.treeDBH, WASMApplicationState.speciesDensity]);

  useEffect(() => {

    if (results.data.length === 0 || avgPoint[1] === -1 || currentSpecies.index === -1) {
      return;
    }

    CalculateCanopyCoverDBHRRBySpecies(currentSpecies.index, WASMApplicationState.speciesDensity[currentSpecies.index], WASMApplicationState.slope * (Math.PI / 180)).then((returnMsg) => {
      setCanopyCoverChartDBH(returnMsg.data);
    }).catch(ex => {

      console.error(ex);
    });

    // Set the mark
    const r = results.data[0].r;
    const lrr = r.rootLateral;

    CalculatePurpleWye(WASMApplicationState, currentSpecies.index).then((returnMsg) => {
      console.log("Return message: ");
      console.log(returnMsg);
      setMarkData({ lrr: returnMsg.data.lrr / 1000, canopy_cover: returnMsg.data.canopy_cover * 100 });
    }).catch(ex => {
      console.error(ex);
      setMarkData({ lrr: -1, canopy_cover: -1 });
    });
  }, [results.data, currentSpecies.index, avgPoint]);


  return (
    <Card style={{ marginBottom: "1em" }}>
      <Card.Header style={{ padding: "1em" }}>
        {currentSpecies.name}{" "}
      </Card.Header>
      <Card.Body>

        <Row>
          <Col xs={12} xl={6}>
            <CanopyCoverChart id={`canopy_cover_${currentSpecies.name}`}
              rrParams={rootReinforcementParams}
              beta={canopyCoverChartBeta}
              dbh={canopyCoverChartDBH}
              mark={markData}
              metrics={results.metrics}
              design_value={WASMApplicationState.characteristicValueCoefficient * WASMApplicationState.gammaRootReinforcement}
            />
          </Col>
          <Col xs={12} xl={6}>
            <ScatterChart
              id={`canopy_cover_${currentSpecies.name}`}
              data={data}
              labels={labels}
              avgPoint={avgPoint}
              species_id={currentSpecies.index}
            />
          </Col>
        </Row>

      </Card.Body>
    </Card>
  );
}

export default StandStateDiagramViewRow;
